<template>
  <div class="flex mb-12 exam-form">
    <div class="w-3/5 content-left-panel">
      <vs-alert
        color="warning"
        icon-pack="feather"
        icon="icon-info"
        class="mb-5"
      >
        Set Exam Type, Question Grouping, and Grading first - these determine
        the CSV
      </vs-alert>
      <div class="flex -mx-2">
        <div class="w-1/2 px-2">
          <label class="vs-input--label">Exam type</label>
          <v-select
            v-model="examType"
            :clearable="false"
            :searchable="false"
            :options="examTypeOptions"
            :reduce="(option) => option.value"
          />
        </div>

        <div class="w-1/2 px-2">
          <label class="vs-input--label">Grading</label>
          <v-select
            v-model="grading"
            :clearable="false"
            :searchable="false"
            :options="gradingOptions"
            :reduce="(option) => option.value"
          />
        </div>
      </div>
      <div class="md:mt-6 mt-4">
        <div class="md:mt-10 mt-6 mb-4">
          <h4>Question grouping</h4>
        </div>
        <div class="flex items-center mb-3">
          <vs-switch v-model="isQuestionTypeGroupingEnabled" class="mr-3" />
          <label>By question type</label>
        </div>
        <div
          v-if="isQuestionTypeGroupingEnabled"
          class="grouping-category-block"
        >
          <div class="flex items-center mb-3">
            <vs-switch
              v-model="isQuestionCategoryGroupingEnabled"
              class="mr-3"
            />
            <label>By question category</label>
          </div>
        </div>
      </div>

      <div class="md:mt-10 mt-6">
        <h4>Randomization</h4>
      </div>
      <div class="md:mt-3 mt-2">
        <div
          v-if="showRandomizeModeSelector"
          class="mb-4 randomize-mode-select"
        >
          <label class="vs-input--label">Randomize questions mode</label>
          <v-select
            v-model="randomizeQuestionsMode"
            :clearable="false"
            :searchable="false"
            :options="computedRandomizeModeOptions"
            :reduce="(option) => option.value"
          />
        </div>
        <div v-if="showGlobalRandomizeSwitch" class="flex items-center mb-4">
          <vs-switch v-model="randomizeQuestions" class="mr-3" />
          <label>Randomize Questions</label>
        </div>
        <div v-if="showPerQuestionTypeSwitches">
          <div class="flex items-center mb-3">
            <vs-switch v-model="randomizeStandAloneQuestions" class="mr-3" />
            <label>Randomize stand alone questions</label>
          </div>
          <div class="flex items-center mb-3">
            <vs-switch v-model="randomizeLabRelatedQuestions" class="mr-3" />
            <label>Randomize lab related questions</label>
          </div>
        </div>
        <small
          v-if="previewUpdatedFields.randomizeQuestions"
          class="preview-source"
        >
          From preview CSV
        </small>
      </div>

      <div class="md:mt-10 mt-6">
        <h4>Exam Composition</h4>
      </div>

      <div class="flex md:mt-6 mt-4 -mx-2">
        <div class="w-1/2 px-2">
          <label class="vs-input--label"># of Questions</label>
          <vs-input
            v-model.number="amountQuestions"
            type="number"
            min="0"
            :disabled="!canEditAmountQuestions"
          />
          <small
            v-if="previewUpdatedFields.amountQuestions"
            class="preview-source"
          >
            From preview CSV
          </small>
        </div>

        <div class="w-1/2 px-2">
          <label class="vs-input--label">Duration (minutes)</label>
          <vs-input v-model.number="durationMinutes" type="number" min="0" />
          <small
            v-if="previewUpdatedFields.durationMinutes"
            class="preview-source"
          >
            From preview CSV
          </small>
        </div>
      </div>

      <div class="flex md:mt-6 mt-4 -mx-2">
        <div class="w-1/2 px-2">
          <label class="vs-input--label">Lab Type</label>
          <v-select
            v-model="labType"
            :clearable="false"
            :searchable="false"
            :options="labTypeOptions"
            :reduce="(option) => option.value"
          />
        </div>
        <div class="w-1/2 px-2">
          <label class="vs-input--label">Lab ID</label>
          <vs-input
            v-model="selectedLabId"
            :class="{ 'input-error': labFieldErrors.selectedLabId }"
          />
          <small v-if="labFieldErrors.selectedLabId" class="field-error-msg">
            {{ labFieldErrors.selectedLabId }}
          </small>
        </div>
      </div>

      <div class="md:mt-10 mt-6">
        <h4>Exam Composition CSV</h4>
      </div>

      <div class="flex md:mt-6 mt-4 flex-wrap items-center">
        <input
          ref="csvInput"
          type="file"
          accept=".csv,text/csv"
          class="hidden"
          @change="onCsvSelected"
        />

        <vx-tooltip
          v-if="!canReplaceCsv"
          text="Published exams require a new minor version to update the CSV."
        >
          <span>
            <vs-button
              color="primary"
              :disabled="isUploadingCsv || !canReplaceCsv"
              @click="$refs.csvInput.click()"
            >
              {{ isUploadingCsv ? 'Uploading CSV...' : 'Upload CSV' }}
            </vs-button>
          </span>
        </vx-tooltip>
        <vs-button
          v-else
          color="primary"
          :disabled="isUploadingCsv || !canReplaceCsv"
          @click="$refs.csvInput.click()"
        >
          {{ isUploadingCsv ? 'Uploading CSV...' : 'Upload CSV' }}
        </vs-button>
        <vs-button
          v-if="currentCsvHash"
          type="border"
          class="ml-2"
          :disabled="isDownloadingCurrentExamCsv"
          @click="downloadCurrentExamCsv"
        >
          {{
            isDownloadingCurrentExamCsv
              ? 'Downloading current CSV exam...'
              : 'Download current CSV exam'
          }}
        </vs-button>

        <vs-button
          type="border"
          class="ml-2"
          :disabled="isDownloadingTemplateCsv"
          @click="downloadTemplate"
        >
          {{
            isDownloadingTemplateCsv
              ? 'Downloading template...'
              : 'Download Template'
          }}
        </vs-button>
      </div>
      <p v-if="activeCsvFileName" class="mt-1 text-sm text-grey">
        Current CSV file: {{ activeCsvFileName }}
      </p>
      <p v-if="!canReplaceCsv" class="mt-1 text-sm text-grey">
        To replace the CSV for a Published exam, create a new minor version.
      </p>

      <p class="mt-3 text-sm text-grey">
        {{ csvColumnsGuidance }}
      </p>
      <csv-diagnostics-accordion
        class="mt-3"
        :diagnostics="csvDiagnostics"
        :loading="isUploadingCsv"
        :show-category-diagnostics="showCategoryDiagnostics"
      />

      <p
        v-if="csvPreviewMessage"
        class="mt-2 text-sm"
        :class="csvPreviewStatus === 'success' ? 'text-success' : 'text-danger'"
      >
        {{ csvPreviewMessage }}
      </p>
      <p v-if="isSaveBlockedByDiagnostics" class="mt-1 text-sm text-danger">
        Save is blocked: CSV diagnostics contain errors.
      </p>

      <div class="md:mt-10 mt-6">
        <h4>Instructions</h4>
      </div>

      <div class="md:mt-6 mt-4">
        <label class="vs-input--label">Exam instructions text</label>
        <markdown-editor
          v-model="examInnerInstructionsMd"
          link-insert-mode="html"
          :options="{
            hideModeSwitch: true,
            previewStyle: 'tab',
            autofocus: false,
          }"
          height="400px"
        />
      </div>

      <div class="md:mt-10 mt-6">
        <label class="vs-input--label">Lab instructions text</label>
        <markdown-editor
          v-model="labInnerInstructionsMd"
          link-insert-mode="html"
          :options="{
            hideModeSwitch: true,
            previewStyle: 'tab',
            autofocus: false,
          }"
          height="400px"
        />
      </div>
    </div>

    <div class="w-2/5 pl-10 content-right-panel">
      <div class="flex mb-2 -mx-2">
        <div class="w-1/2 px-2 created-by-name">
          <label class="vs-input--label">Created by</label>
          <vs-input class="w-full" disabled :value="displayCreatedByName" />
        </div>
        <div class="w-1/2 px-2 created-date">
          <label class="vs-input--label">Created on</label>
          <vs-input class="w-full" disabled :value="displayCreated | dateFormat" />
        </div>
      </div>
      <div class="flex mb-10 -mx-2">
        <div class="w-1/2 px-2 modified-by-name">
          <label class="vs-input--label">Modified by</label>
          <vs-input class="w-full" disabled :value="modifiedByName" />
        </div>
        <div class="w-1/2 px-2 modified-date">
          <label class="vs-input--label">Modified on</label>
          <vs-input class="w-full" disabled :value="modified | dateFormat" />
        </div>
      </div>
      <div class="content-status mb-5">
        <label class="vs-input--label">Status</label>
        <v-select
          v-model="status"
          :clearable="false"
          :searchable="false"
          :options="statusOptions"
          :reduce="(option) => option.value"
          :disabled="isParentCertificationDraft || !canUpdateStatus"
        />
      </div>
      <div class="exam-preview-box mb-5" v-if="canPreviewExam">
        <div class="flex items-center mb-3">
          <vs-switch v-model="previewWithAllQuestions" class="mr-3" />
          <label>Preview with all questions</label>
        </div>
        <div class="flex -mx-2 action-buttons">
          <vs-button class="w-full mx-2" type="border" @click="openExamPreview">
            Exam Preview
          </vs-button>
        </div>
      </div>
      <div class="flex -mx-2 action-buttons mt-5">
        <vs-button
          class="w-full mx-2"
          color="primary"
          icon-pack="feather"
          :disabled="isSaving || isSaveBlockedByDiagnostics"
          @click="saveExam"
        >
          {{ isSaving ? 'Saving exam...' : 'Save exam' }}
        </vs-button>
      </div>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep'
import isEqual from 'lodash.isequal'
import { mapActions, mapGetters } from 'vuex'
import vSelect from 'vue-select'
import CONSTANTS from '@/constants'
import { ROLE_ADMIN } from '@/constants/roles'
import MarkdownEditor from '@/components/markdownEditor'
import { hasBlockingDiagnosticsStatus } from '@/utils/csvDiagnostics'
import {
  getExamPublishValidationErrors,
  mapPublishValidationErrorsToLabels,
} from '@/services/adapters/certificationAdapter'
import CsvDiagnosticsAccordion from './components/CsvDiagnosticsAccordion'

const RANDOMIZE_MODE = {
  GLOBAL: 'global',
  BY_QUESTION_TYPE: 'by_question_type',
}

const LAB_TYPE = {
  QUESTION: 'question',
  PRACTICE: 'practice',
}

const QUESTION_GROUPING_FIELDS = {
  QUESTION_TYPE: 'question_type',
  QUESTION_CATEGORY: 'question_category',
}

const QUESTION_GROUPING_SCOPES = {
  LAB_ONLY: 'lab_only',
}

const EXAM_DEFAULTS = {
  examType: CONSTANTS.CERTIFICATION_EXAM_TYPES.BY_QUESTION_CATEGORY,
  questionGrouping: [],
  grading: CONSTANTS.CERTIFICATION_GRADING_TYPES.REGULAR,
  useQuestionCategories: true,
  hasDomainMinScores: false,
  randomizeQuestions: false,
  amountQuestions: 0,
  durationMinutes: 48,
  minGlobalScoreToPass: 0.7,
  examInnerInstructionsMd: '',
  labInnerInstructionsMd: '',
  practiceLabId: '',
  questionsLabId: '',
}

export default {
  name: 'exam-form',

  components: {
    'v-select': vSelect,
    MarkdownEditor,
    CsvDiagnosticsAccordion,
  },

  props: {
    exam: {
      type: Object,
      default: null,
    },
    prefillExam: {
      type: Object,
      default: null,
    },
    isParentCertificationDraft: {
      type: Boolean,
      default: false,
    },
    ensureExamExists: {
      type: Function,
      required: true,
    },
    requestChangeNote: {
      type: Function,
      required: true,
    },
  },

  data() {
    return {
      selectedCsvFile: null,
      csvPreviewStatus: '',
      csvPreviewMessage: '',
      initialCsvHash: null,
      previewCsvHash: null,
      previewValidated: false,
      isUploadingCsv: false,
      hasAppliedPrefill: false,
      isDownloadingCurrentExamCsv: false,
      isDownloadingTemplateCsv: false,
      isSaving: false,
      labFieldErrors: {},
      myIneUrl: process.env.VUE_APP_MY_INE_URL,
      previewWithAllQuestions: false,
      previewUpdatedFields: {},
      csvDiagnostics: null,
      initialExamPayload: null,
      status: CONSTANTS.CERTIFICATION_STATUSES.DRAFT,

      examType: EXAM_DEFAULTS.examType,
      questionGrouping: [...EXAM_DEFAULTS.questionGrouping],
      grading: EXAM_DEFAULTS.grading,
      useQuestionCategories: EXAM_DEFAULTS.useQuestionCategories,
      hasDomainMinScores: EXAM_DEFAULTS.hasDomainMinScores,
      randomizeQuestions: EXAM_DEFAULTS.randomizeQuestions,
      randomizeQuestionsMode: RANDOMIZE_MODE.GLOBAL,
      randomizeStandAloneQuestions: false,
      randomizeLabRelatedQuestions: false,
      amountQuestions: EXAM_DEFAULTS.amountQuestions,
      durationMinutes: EXAM_DEFAULTS.durationMinutes,
      minGlobalScoreToPass: EXAM_DEFAULTS.minGlobalScoreToPass,
      examInnerInstructionsMd: EXAM_DEFAULTS.examInnerInstructionsMd,
      labInnerInstructionsMd: EXAM_DEFAULTS.labInnerInstructionsMd,
      labType: LAB_TYPE.QUESTION,
      practiceLabId: EXAM_DEFAULTS.practiceLabId,
      questionsLabId: EXAM_DEFAULTS.questionsLabId,
      examTypeOptions: [
        {
          label: 'By Objective',
          value: CONSTANTS.CERTIFICATION_EXAM_TYPES.BY_OBJECTIVE,
        },
        {
          label: 'By Question Category',
          value: CONSTANTS.CERTIFICATION_EXAM_TYPES.BY_QUESTION_CATEGORY,
        },
      ],
      gradingOptions: [
        {
          label: 'Regular',
          value: CONSTANTS.CERTIFICATION_GRADING_TYPES.REGULAR,
        },
        {
          label: 'Per Question Weight',
          value: CONSTANTS.CERTIFICATION_GRADING_TYPES.PER_QUESTION_WEIGHT,
        },
      ],
      labTypeOptions: [
        { label: 'Question', value: LAB_TYPE.QUESTION },
        { label: 'Practice', value: LAB_TYPE.PRACTICE },
      ],
      randomizeModeOptions: [
        { label: 'Global', value: RANDOMIZE_MODE.GLOBAL },
        {
          label: 'By question type',
          value: RANDOMIZE_MODE.BY_QUESTION_TYPE,
          disabled: false,
        },
      ],
    }
  },
  watch: {
    exam: {
      immediate: true,
      handler(exam) {
        this.hydrateFromExam(exam)
      },
    },
    statusSource: {
      immediate: true,
      handler(status) {
        this.status = status
      },
    },
    examType() {
      this.recalculateAmountQuestions()
    },
    labType() {
      this.clearLabFieldError('selectedLabId')
    },
    selectedLabId() {
      this.clearLabFieldError('selectedLabId')
    },
    randomizeQuestionsMode(nextMode) {
      if (nextMode === RANDOMIZE_MODE.BY_QUESTION_TYPE) {
        this.randomizeQuestions = false
        this.ensureQuestionTypeGrouping()
      }
    },
  },
  methods: {
    ...mapActions('certification', [
      'getCertificationExamAction',
      'updateCertificationExamAction',
      'validateCertificationExamCsvAction',
      'uploadCertificationExamCsvAction',
      'downloadCertificationExamCsvAction',
      'downloadCertificationExamTemplateCsvAction',
    ]),
    openDownloadUrl(downloadUrl) {
      if (!downloadUrl) {
        throw new Error('Missing download URL')
      }
      window.open(downloadUrl, '_self')
    },
    openExamPreview() {
      if (!this.examPreviewUrl) return
      if (!this.hasSelectedLabId) {
        this.setLabFieldErrors({
          selectedLabId: 'Lab ID is required to preview the exam.',
        })
        this.$vs.notify({
          title: 'Lab ID required',
          text: 'Add a Lab ID before previewing the exam.',
          color: 'warning',
        })
        return
      }
      window.open(this.examPreviewUrl, '_blank')
    },
    normalizeLabId(value) {
      return typeof value === 'string' ? value.trim() : value
    },
    setLabFieldErrors(errors = {}) {
      this.labFieldErrors = errors
    },
    clearLabFieldError(field) {
      if (!this.labFieldErrors[field]) return

      const nextErrors = { ...this.labFieldErrors }
      delete nextErrors[field]
      this.labFieldErrors = nextErrors
    },
    isDirty() {
      return this.hasPendingExamChanges
    },
    onCsvSelected(event) {
      if (!this.canReplaceCsv) return

      const file = event.target.files && event.target.files[0]
      this.selectedCsvFile = file || null

      if (this.selectedCsvFile) {
        this.uploadCsv()
      }
    },
    buildRandomizePerQuestionTypeConfig() {
      if (this.randomizeQuestionsMode !== RANDOMIZE_MODE.BY_QUESTION_TYPE) {
        return null
      }

      return {
        stand_alone: Boolean(this.randomizeStandAloneQuestions),
        lab_related: Boolean(this.randomizeLabRelatedQuestions),
      }
    },
    ensureQuestionTypeGroupingFor(questionGrouping) {
      const normalizedQuestionGrouping =
        this.normalizeQuestionGrouping(questionGrouping)

      return normalizedQuestionGrouping.length
        ? normalizedQuestionGrouping
        : [{ field: QUESTION_GROUPING_FIELDS.QUESTION_TYPE }]
    },
    ensureQuestionTypeGrouping() {
      this.questionGrouping = this.ensureQuestionTypeGroupingFor(
        this.questionGrouping
      )
    },
    buildFullPayload() {
      const examConfig = this.buildExamConfig()
      return {
        status: this.status,
        exam_type: this.examType,
        amount_questions: examConfig.amount_questions,
        question_grouping: this.normalizeQuestionGrouping(
          this.questionGrouping
        ),
        min_global_score_to_pass: examConfig.min_global_score_to_pass,
        time_cap_in_secs: examConfig.time_cap_in_secs,
        randomize_questions: examConfig.randomize_questions,
        randomize_questions_per_question_type:
          examConfig.randomize_questions_per_question_type,
        grading_per_weight: examConfig.grading_per_weight,
        practice_lab_id:
          this.labType === LAB_TYPE.PRACTICE
            ? this.practiceLabId || null
            : null,
        questions_lab_id:
          this.labType === LAB_TYPE.QUESTION
            ? this.questionsLabId || null
            : null,
        exam_inner_instructions_md: this.examInnerInstructionsMd || '',
        lab_inner_instructions_md: this.labInnerInstructionsMd || '',
      }
    },
    buildPatchPayload() {
      if (!this.initialExamPayload) return {}

      const fullPayload = this.buildFullPayload()
      const initialPayload = this.initialExamPayload || {}

      return Object.fromEntries(
        Object.entries(fullPayload).filter(
          ([key, value]) =>
            (key !== 'status' || this.canUpdateStatus) &&
            !isEqual(value, initialPayload[key])
        )
      )
    },
    appendChangeNoteToPayload(payload, changeNote) {
      if (!changeNote) return payload

      return {
        ...payload,
        change_note: changeNote,
      }
    },
    buildPublishValidationCandidate() {
      return {
        ...(this.exam || {}),
        ...this.buildFullPayload(),
        csv_hash: this.currentCsvHash,
      }
    },
    buildExamConfig() {
      return {
        amount_questions: Number(this.amountQuestions || 0),
        min_global_score_to_pass: Number(this.minGlobalScoreToPass || 0),
        time_cap_in_secs: Number(this.durationMinutes || 0) * 60,
        randomize_questions: Boolean(this.randomizeQuestions),
        randomize_questions_per_question_type:
          this.buildRandomizePerQuestionTypeConfig(),
        grading_per_weight:
          this.grading ===
          CONSTANTS.CERTIFICATION_GRADING_TYPES.PER_QUESTION_WEIGHT,
      }
    },

    appendCsvCommonFields(formData) {
      const examConfig = this.buildExamConfig()
      formData.append('exam_type', this.examType)
      formData.append(
        'question_grouping',
        JSON.stringify(this.questionGrouping)
      )
      formData.append('grading', this.grading)
      formData.append(
        'use_question_categories',
        String(this.useQuestionCategories)
      )
      formData.append('has_domain_min_scores', String(this.hasDomainMinScores))
      formData.append(
        'min_global_score_to_pass',
        String(examConfig.min_global_score_to_pass)
      )
      formData.append('time_cap_in_secs', String(examConfig.time_cap_in_secs))
      formData.append(
        'randomize_questions',
        String(examConfig.randomize_questions)
      )
      formData.append(
        'randomize_questions_per_question_type',
        JSON.stringify(
          examConfig.randomize_questions_per_question_type === null
            ? null
            : examConfig.randomize_questions_per_question_type
        )
      )
      formData.append(
        'select_questions_per_objective_per_question_category',
        String(this.selectQuestionsPerObjectivePerQuestionCategory)
      )
      formData.append(
        'grading_per_weight',
        String(examConfig.grading_per_weight)
      )
      formData.append(
        'practice_lab_id',
        this.labType === LAB_TYPE.PRACTICE ? this.practiceLabId || '' : ''
      )
      formData.append(
        'questions_lab_id',
        this.labType === LAB_TYPE.QUESTION ? this.questionsLabId || '' : ''
      )
    },

    buildCsvPreviewFormData() {
      const formData = new FormData()

      formData.append(
        'certification_version_id',
        this.exam.certification_version_id
      )
      formData.append('file', this.selectedCsvFile)
      this.appendCsvCommonFields(formData)

      return formData
    },

    buildCsvUploadFormData(examId) {
      const formData = new FormData()

      formData.append('exam_id', examId)
      formData.append('file', this.selectedCsvFile)
      this.appendCsvCommonFields(formData)

      return formData
    },

    shouldUploadPreviewCsv() {
      if (!this.canReplaceCsv) return false
      if (!this.selectedCsvFile) return false
      if (!this.previewValidated || this.csvPreviewStatus !== 'success')
        return false
      if (!this.initialCsvHash) return true
      if (!this.previewCsvHash) return true

      return this.previewCsvHash !== this.initialCsvHash
    },
    markPreviewField(fieldName) {
      this.previewUpdatedFields = {
        ...this.previewUpdatedFields,
        [fieldName]: true,
      }
    },
    hasBlockingDiagnostics(diagnostics) {
      const status = diagnostics && diagnostics.status
      if (!status) return false

      return hasBlockingDiagnosticsStatus(status, this.showCategoryDiagnostics)
    },
    applyPreviewConfig(response = {}) {
      const config = response.config || response.exam_config || {}

      if (config.randomize_questions !== undefined) {
        this.randomizeQuestions = Boolean(config.randomize_questions)
        this.markPreviewField('randomizeQuestions')
      }
      if (
        config.randomize_questions_per_question_type &&
        typeof config.randomize_questions_per_question_type === 'object'
      ) {
        this.randomizeQuestionsMode = RANDOMIZE_MODE.BY_QUESTION_TYPE
        this.randomizeStandAloneQuestions = Boolean(
          config.randomize_questions_per_question_type.stand_alone
        )
        this.randomizeLabRelatedQuestions = Boolean(
          config.randomize_questions_per_question_type.lab_related
        )
      } else {
        this.randomizeQuestionsMode = RANDOMIZE_MODE.GLOBAL
        this.randomizeStandAloneQuestions = false
        this.randomizeLabRelatedQuestions = false
      }
      if (
        config.amount_questions !== undefined &&
        config.amount_questions !== null
      ) {
        this.amountQuestions = Number(config.amount_questions)
        this.markPreviewField('amountQuestions')
      }
      if (
        config.time_cap_in_secs !== undefined &&
        config.time_cap_in_secs !== null
      ) {
        this.durationMinutes = Math.floor(Number(config.time_cap_in_secs) / 60)
        this.markPreviewField('durationMinutes')
      }
      if (
        response.randomize_questions !== undefined &&
        config.randomize_questions === undefined
      ) {
        this.randomizeQuestions = Boolean(response.randomize_questions)
        this.markPreviewField('randomizeQuestions')
      }
      if (
        response.amount_questions !== undefined &&
        response.amount_questions !== null &&
        config.amount_questions === undefined
      ) {
        this.amountQuestions = Number(response.amount_questions)
        this.markPreviewField('amountQuestions')
      }
      if (
        response.time_cap_in_secs !== undefined &&
        response.time_cap_in_secs !== null &&
        config.time_cap_in_secs === undefined
      ) {
        this.durationMinutes = Math.floor(
          Number(response.time_cap_in_secs) / 60
        )
        this.markPreviewField('durationMinutes')
      }
      this.csvDiagnostics = response.preview_diagnostics || null
    },
    inferGrading(exam, config = {}, questionsPool = []) {
      if (config.grading_per_weight === true)
        return CONSTANTS.CERTIFICATION_GRADING_TYPES.PER_QUESTION_WEIGHT
      if (config.grading_per_weight === false)
        return CONSTANTS.CERTIFICATION_GRADING_TYPES.REGULAR

      const hasCustomWeights = (questionsPool || []).some((question) => {
        const weight = Number(question && question.weight)
        return !Number.isNaN(weight) && weight !== 1
      })

      return hasCustomWeights
        ? CONSTANTS.CERTIFICATION_GRADING_TYPES.PER_QUESTION_WEIGHT
        : CONSTANTS.CERTIFICATION_GRADING_TYPES.REGULAR
    },
    inferExamType(exam, config = {}, questionsPool = []) {
      // Best-effort inference for legacy/incomplete persisted exam data.
      const normalizedQuestionGrouping = this.normalizeQuestionGrouping(
        exam && exam.question_grouping
      )
      if (normalizedQuestionGrouping.length) {
        const hasQuestionCategory = normalizedQuestionGrouping.some(
          (item) =>
            item && item.field === QUESTION_GROUPING_FIELDS.QUESTION_CATEGORY
        )
        return hasQuestionCategory
          ? CONSTANTS.CERTIFICATION_EXAM_TYPES.BY_QUESTION_CATEGORY
          : EXAM_DEFAULTS.examType
      }

      const hasObjectiveConfig = Boolean(
        (config.amount_questions_per_objective &&
          Object.keys(config.amount_questions_per_objective).length) ||
          (config.min_score_per_objective_to_pass &&
            Object.keys(config.min_score_per_objective_to_pass).length)
      )
      if (hasObjectiveConfig) {
        return CONSTANTS.CERTIFICATION_EXAM_TYPES.BY_OBJECTIVE
      }

      const hasSelectPerCategory = Boolean(
        config.select_questions_per_objective_per_question_category
      )
      const hasConfigCategories = Boolean(
        (config.question_categories &&
          Object.keys(config.question_categories).length) ||
          (config.amount_questions_per_question_category &&
            Object.keys(config.amount_questions_per_question_category).length)
      )
      const hasQuestionCategories = (questionsPool || []).some(
        (question) => question && question.category
      )

      if (
        hasSelectPerCategory ||
        hasConfigCategories ||
        hasQuestionCategories
      ) {
        return CONSTANTS.CERTIFICATION_EXAM_TYPES.BY_QUESTION_CATEGORY
      }

      return EXAM_DEFAULTS.examType
    },
    inferQuestionGrouping(exam, config = {}, questionsPool = []) {
      const normalizedQuestionGrouping = this.normalizeQuestionGrouping(
        exam && exam.question_grouping
      )
      if (normalizedQuestionGrouping.length) {
        return normalizedQuestionGrouping
      }

      // Best-effort inference for legacy/incomplete persisted exam data.
      const hasLabRelatedQuestions = (questionsPool || []).some((question) =>
        Boolean(question && question.is_lab_related)
      )
      const hasLabRelatedConfig = Boolean(
        config.lab_related ||
          config.amount_questions_lab_related ||
          config.amount_questions_per_objective_lab_related
      )

      if (hasLabRelatedQuestions || hasLabRelatedConfig) {
        return [
          { field: QUESTION_GROUPING_FIELDS.QUESTION_TYPE },
          {
            field: QUESTION_GROUPING_FIELDS.QUESTION_CATEGORY,
            scope: QUESTION_GROUPING_SCOPES.LAB_ONLY,
          },
        ]
      }

      return [{ field: QUESTION_GROUPING_FIELDS.QUESTION_TYPE }]
    },
    normalizeQuestionGrouping(questionGrouping = []) {
      if (!Array.isArray(questionGrouping) || !questionGrouping.length)
        return []

      const hasQuestionType = questionGrouping.some(
        (item) => item && item.field === QUESTION_GROUPING_FIELDS.QUESTION_TYPE
      )
      const categoryGrouping = questionGrouping.find(
        (item) =>
          item && item.field === QUESTION_GROUPING_FIELDS.QUESTION_CATEGORY
      )

      if (categoryGrouping) {
        return [
          { field: QUESTION_GROUPING_FIELDS.QUESTION_TYPE },
          {
            field: QUESTION_GROUPING_FIELDS.QUESTION_CATEGORY,
            scope: QUESTION_GROUPING_SCOPES.LAB_ONLY,
          },
        ]
      }

      if (hasQuestionType) {
        return [{ field: QUESTION_GROUPING_FIELDS.QUESTION_TYPE }]
      }

      return []
    },
    hasValue(value) {
      return value !== undefined && value !== null
    },
    safeNumber(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },
    totalQuestionsFromObjectives(config = {}) {
      const perObjective = config.amount_questions_per_objective || {}
      return Object.values(perObjective).reduce((acc, objectiveConfig) => {
        if (
          objectiveConfig &&
          typeof objectiveConfig === 'object' &&
          !Array.isArray(objectiveConfig)
        ) {
          const standAlone = this.safeNumber(objectiveConfig.stand_alone)
          const labRelated = this.safeNumber(objectiveConfig.lab_related)
          return acc + standAlone + labRelated
        }
        return acc + this.safeNumber(objectiveConfig)
      }, 0)
    },
    totalQuestionsFromCategories(config = {}) {
      const questionCategories = config.question_categories || {}
      return Object.values(questionCategories).reduce(
        (acc, categoryQuestions) => acc + this.safeNumber(categoryQuestions),
        0
      )
    },
    recalculateAmountQuestions(configOverride = null) {
      const config =
        configOverride || (this.exam && this.exam.config) || {}
      const derivedAmountQuestions =
        this.examType === CONSTANTS.CERTIFICATION_EXAM_TYPES.BY_OBJECTIVE
          ? this.totalQuestionsFromObjectives(config)
          : this.totalQuestionsFromCategories(config)
      const fallbackAmountQuestions = this.safeNumber(config.amount_questions)
      this.amountQuestions =
        derivedAmountQuestions > 0
          ? derivedAmountQuestions
          : fallbackAmountQuestions
    },

    hydrateFromExam(exam) {
      const prefillExam = this.prefillExam || null
      const hasPrefillExam =
        Boolean(prefillExam) && Object.keys(prefillExam).length > 0
      const shouldUsePrefill = hasPrefillExam && !this.hasAppliedPrefill
      let sourceExam = shouldUsePrefill ? prefillExam : exam || {}
      const config = sourceExam.config || {}
      const questionsPool = sourceExam.questions_pool || []
      const inferredOptions = (sourceExam && sourceExam.inferred_options) || {}
      if (shouldUsePrefill) {
        this.hasAppliedPrefill = true
      }

      this.grading =
        inferredOptions.grading ||
        this.inferGrading(sourceExam, config, questionsPool) ||
        EXAM_DEFAULTS.grading
      this.examType =
        inferredOptions.exam_type ||
        this.inferExamType(sourceExam, config, questionsPool) ||
        EXAM_DEFAULTS.examType
      const hasExplicitQuestionGrouping =
        Array.isArray(sourceExam.question_grouping) ||
        Array.isArray(exam && exam.question_grouping)
      const normalizedQuestionGrouping = this.normalizeQuestionGrouping(
        inferredOptions.question_grouping
      )
      this.questionGrouping = hasExplicitQuestionGrouping
        ? this.normalizeQuestionGrouping(
            sourceExam.question_grouping ||
              (exam && exam.question_grouping) ||
              []
          )
        : normalizedQuestionGrouping.length
        ? normalizedQuestionGrouping
        : this.inferQuestionGrouping(sourceExam, config, questionsPool)
      this.recalculateAmountQuestions(config)
      const timeCapInSecs =
        sourceExam && this.hasValue(sourceExam.time_cap_in_secs)
          ? sourceExam.time_cap_in_secs
          : this.hasValue(config.time_cap_in_secs)
          ? config.time_cap_in_secs
          : EXAM_DEFAULTS.durationMinutes * 60
      this.durationMinutes = Math.floor(Number(timeCapInSecs) / 60)
      this.minGlobalScoreToPass = Number(
        config.min_global_score_to_pass !== undefined &&
          config.min_global_score_to_pass !== null
          ? config.min_global_score_to_pass
          : EXAM_DEFAULTS.minGlobalScoreToPass
      )

      this.randomizeQuestions = Boolean(
        config.randomize_questions !== undefined
          ? config.randomize_questions
          : sourceExam && sourceExam.randomize_questions
      )
      if (
        config.randomize_questions_per_question_type &&
        typeof config.randomize_questions_per_question_type === 'object'
      ) {
        this.randomizeQuestionsMode = RANDOMIZE_MODE.BY_QUESTION_TYPE
        this.randomizeStandAloneQuestions = Boolean(
          config.randomize_questions_per_question_type.stand_alone
        )
        this.randomizeLabRelatedQuestions = Boolean(
          config.randomize_questions_per_question_type.lab_related
        )
      } else {
        this.randomizeQuestionsMode = RANDOMIZE_MODE.GLOBAL
        this.randomizeStandAloneQuestions = false
        this.randomizeLabRelatedQuestions = false
      }

      this.examInnerInstructionsMd =
        (sourceExam && sourceExam.exam_inner_instructions_md) ||
        EXAM_DEFAULTS.examInnerInstructionsMd
      this.labInnerInstructionsMd =
        (sourceExam && sourceExam.lab_inner_instructions_md) ||
        EXAM_DEFAULTS.labInnerInstructionsMd
      this.practiceLabId =
        (sourceExam && sourceExam.practice_lab_id) ||
        EXAM_DEFAULTS.practiceLabId
      this.questionsLabId =
        (sourceExam && sourceExam.questions_lab_id) ||
        EXAM_DEFAULTS.questionsLabId
      this.labType = this.questionsLabId
        ? LAB_TYPE.QUESTION
        : this.practiceLabId
        ? LAB_TYPE.PRACTICE
        : LAB_TYPE.QUESTION
      this.initialCsvHash = (exam && exam.csv_hash) || null
      this.previewUpdatedFields = {}
      this.csvDiagnostics = (exam && exam.preview_diagnostics) || null
      this.status = this.statusSource
      this.initialExamPayload = cloneDeep(this.buildFullPayload())
    },
    async downloadCurrentExamCsv() {
      if (
        !this.exam ||
        !this.exam.id ||
        this.isDownloadingCurrentExamCsv
      )
        return

      this.isDownloadingCurrentExamCsv = true
      try {
        const response = await this.downloadCertificationExamCsvAction({
          examId: this.exam.id,
        })
        this.openDownloadUrl(response && response.download_url)
      } catch (error) {
        this.showError(error)
      } finally {
        this.isDownloadingCurrentExamCsv = false
      }
    },

    async saveExam() {
      const hasStatusUpdate = this.hasPendingStatusChange

      try {
        let payload = this.buildPatchPayload()
        const csvWillBeUploaded = this.shouldUploadPreviewCsv()
        if (!Object.keys(payload).length && !csvWillBeUploaded) return
        let changeNote = null

        if (
          hasStatusUpdate &&
          this.status === CONSTANTS.CERTIFICATION_STATUSES.PUBLISHED &&
          !this.canPublishCurrentVersion
        ) {
          this.$vs.notify({
            title: 'Missing required value(s)',
            text: `Complete required publishing fields before publishing:\n- ${this.publishValidationErrorLabels.join(
              '\n- '
            )}`,
            color: 'warning',
            time: '12000',
          })
          return
        }

        if (
          this.certificationChangeNotesEnabled &&
          hasStatusUpdate &&
          this.status === CONSTANTS.CERTIFICATION_STATUSES.PUBLISHED
        ) {
          changeNote = await this.requestChangeNote()
          if (!changeNote) return
        } else if (
          this.certificationChangeNotesEnabled &&
          this.isPersistedPublishedStatus
        ) {
          changeNote = await this.requestChangeNote()
          if (!changeNote) return
        }

        if (changeNote) {
          payload = this.appendChangeNoteToPayload(payload, changeNote)
        }

        this.isSaving = true
        this.$vs.loading()

        const examId = await this.ensureExamExists()

        if (csvWillBeUploaded) {
          const uploadFormData = this.buildCsvUploadFormData(examId)

          const uploadResponse = await this.uploadCertificationExamCsvAction({
            formData: uploadFormData,
          })
          this.csvDiagnostics =
            (uploadResponse && uploadResponse.preview_diagnostics) ||
            this.csvDiagnostics
        }

        if (Object.keys(payload).length) {
          await this.updateCertificationExamAction({ examId, payload })
        }

        const freshExam = await this.getCertificationExamAction({ examId })
        this.hydrateFromExam(freshExam)
        this.selectedCsvFile = null
        this.previewValidated = false
        this.previewCsvHash = null
        this.csvPreviewStatus = ''
        this.csvPreviewMessage = ''

        this.$emit('exam-updated', freshExam)

        this.$vs.notify({
          title: 'Saved',
          text: 'Exam configuration was updated successfully.',
          color: 'success',
        })
      } catch (error) {
        this.$vs.notify({
          title: 'Save failed',
          text: 'Exam could not be fully saved. Review the error details.',
          color: 'danger',
        })

        if (error && error.response && error.response.status === 403) {
          this.$router.push({ name: 'page-not-authorized' })
          return
        }

        this.showError(error)
      } finally {
        this.isSaving = false
        this.$vs.loading.close()
      }
    },
    async uploadCsv() {
      if (
        !this.canReplaceCsv ||
        !this.selectedCsvFile ||
        !this.exam ||
        !this.exam.certification_version_id ||
        this.isUploadingCsv
      ) {
        return
      }

      this.isUploadingCsv = true
      try {
        const formData = this.buildCsvPreviewFormData()

        const response = await this.validateCertificationExamCsvAction({
          formData,
        })
        const diagnostics = response && response.preview_diagnostics
        const diagnosticsOk = !this.hasBlockingDiagnostics(diagnostics)

        this.previewValidated = true
        this.previewCsvHash = (response && response.csv_hash) || null
        this.csvPreviewStatus = diagnosticsOk ? 'success' : 'error'
        this.csvPreviewMessage = diagnosticsOk
          ? 'CSV preview validated successfully.'
          : 'CSV preview found diagnostics issues.'
        this.applyPreviewConfig(response || {})

        this.$vs.notify({
          title: diagnosticsOk
            ? 'CSV preview ready'
            : 'CSV preview found errors',
          text: diagnosticsOk
            ? 'CSV was validated successfully (preview only, no upload).'
            : 'CSV preview found diagnostics issues.',
          color: diagnosticsOk ? 'success' : 'danger',
        })
      } catch (error) {
        const responseData = error && error.response && error.response.data
        const diagnostics = responseData && responseData.preview_diagnostics

        if (diagnostics) {
          this.csvDiagnostics = diagnostics
        }
        this.previewValidated = false
        this.previewCsvHash = null
        this.csvPreviewStatus = 'error'
        const notificationText =
          'CSV preview failed. Please review the file and try again.'
        this.csvPreviewMessage = diagnostics
          ? 'CSV preview found diagnostics issues.'
          : (responseData && (responseData.detail || responseData.message)) ||
            notificationText

        this.showError(notificationText)
      } finally {
        this.isUploadingCsv = false
        if (this.$refs.csvInput) {
          this.$refs.csvInput.value = ''
        }
      }
    },

    async downloadTemplate() {
      if (this.isDownloadingTemplateCsv) return

      this.isDownloadingTemplateCsv = true
      try {
        const response = await this.downloadCertificationExamTemplateCsvAction({
          payload: {
            exam_type: this.examType,
            grading: this.grading,
          },
        })
        this.openDownloadUrl(response && response.download_url)
      } catch (error) {
        this.showError(error)
      } finally {
        this.isDownloadingTemplateCsv = false
      }
    },
  },
  computed: {
    ...mapGetters('featureFlags', ['featureFlagsMap']),
    certificationChangeNotesEnabled() {
      return Boolean(
        this.featureFlagsMap && this.featureFlagsMap.certificationChangeNotes
      )
    },
    ...mapGetters('users', ['currentUserRoles']),
    statusSource() {
      return (
        (this.exam && this.exam.status) ||
        CONSTANTS.CERTIFICATION_STATUSES.DRAFT
      )
    },
    isPublishingStatus() {
      return this.status === CONSTANTS.CERTIFICATION_STATUSES.PUBLISHED
    },
    isPersistedPublishedStatus() {
      return this.statusSource === CONSTANTS.CERTIFICATION_STATUSES.PUBLISHED
    },
    isAdminUser() {
      return (this.currentUserRoles || []).includes(ROLE_ADMIN)
    },
    isComingSoonStatus() {
      return this.status === CONSTANTS.CERTIFICATION_STATUSES.COMING_SOON
    },
    canEditAmountQuestions() {
      const editableStatus =
        this.status === CONSTANTS.CERTIFICATION_STATUSES.DRAFT ||
        this.isComingSoonStatus

      return editableStatus && !this.currentCsvHash
    },
    canUpdateStatus() {
      return this.isAdminUser || !this.isPersistedPublishedStatus
    },
    canReplaceCsv() {
      return !this.isPersistedPublishedStatus
    },
    statusOptions() {
      const options = [
        { label: 'Draft', value: CONSTANTS.CERTIFICATION_STATUSES.DRAFT },
        {
          label: 'Coming Soon',
          value: CONSTANTS.CERTIFICATION_STATUSES.COMING_SOON,
        },
        {
          label: 'Published',
          value: CONSTANTS.CERTIFICATION_STATUSES.PUBLISHED,
        },
        {
          label: 'Unpublished',
          value: CONSTANTS.CERTIFICATION_STATUSES.UNPUBLISHED,
        },
      ]

      if (!this.isAdminUser && this.isPersistedPublishedStatus) {
        return options.filter(
          (option) => option.value === CONSTANTS.CERTIFICATION_STATUSES.PUBLISHED
        )
      }

      return options
    },
    publishValidationErrors() {
      return getExamPublishValidationErrors(
        this.buildPublishValidationCandidate()
      )
    },
    canPublishCurrentVersion() {
      return !this.publishValidationErrors.length
    },
    publishValidationErrorLabels() {
      return mapPublishValidationErrorsToLabels(this.publishValidationErrors)
    },
    hasPendingStatusChange() {
      return this.canUpdateStatus && this.status !== this.statusSource
    },
    hasPendingExamChanges() {
      if (this.shouldUploadPreviewCsv()) return true
      return Object.keys(this.buildPatchPayload()).length > 0
    },
    isQuestionTypeGroupingEnabled: {
      get() {
        return this.questionGrouping.length > 0
      },
      set(enabled) {
        if (!enabled) {
          this.questionGrouping = []
          return
        }

        if (!this.questionGrouping.length) {
          this.questionGrouping = [
            { field: QUESTION_GROUPING_FIELDS.QUESTION_TYPE },
          ]
        }
      },
    },
    isQuestionCategoryGroupingEnabled: {
      get() {
        return this.questionGrouping.some(
          (item) =>
            item && item.field === QUESTION_GROUPING_FIELDS.QUESTION_CATEGORY
        )
      },
      set(enabled) {
        if (!enabled) {
          this.questionGrouping = [
            { field: QUESTION_GROUPING_FIELDS.QUESTION_TYPE },
          ]
          return
        }

        this.questionGrouping = [
          { field: QUESTION_GROUPING_FIELDS.QUESTION_TYPE },
          {
            field: QUESTION_GROUPING_FIELDS.QUESTION_CATEGORY,
            scope: QUESTION_GROUPING_SCOPES.LAB_ONLY,
          },
        ]
      },
    },
    selectQuestionsPerObjectivePerQuestionCategory() {
      return (
        this.examType ===
        CONSTANTS.CERTIFICATION_EXAM_TYPES.BY_QUESTION_CATEGORY
      )
    },
    modified() {
      return (this.exam && this.exam.modified) || ''
    },
    created() {
      return (this.exam && this.exam.created) || ''
    },
    createdByName() {
      if (!this.exam) return ''
      return this.exam.created_by_name || this.exam.created_by || ''
    },
    displayCreated() {
      return this.created
    },
    displayCreatedByName() {
      return this.createdByName
    },
    modifiedByName() {
      if (!this.exam) return ''
      return (
        this.exam.last_edited_by_name ||
        this.exam.modified_by_name ||
        this.exam.last_edited_by ||
        this.exam.modified_by ||
        ''
      )
    },
    currentCsvHash() {
      return (
        this.previewCsvHash ||
        (this.exam && this.exam.csv_hash) ||
        this.initialCsvHash ||
        null
      )
    },
    backendCsvFileName() {
      if (!this.exam) return ''

      return (
        this.exam.csv_file_name ||
        this.exam.csv_filename ||
        this.exam.csv_name ||
        this.exam.file_name ||
        ''
      )
    },
    activeCsvFileName() {
      return (
        (this.selectedCsvFile && this.selectedCsvFile.name) ||
        this.backendCsvFileName
      )
    },
    selectedLabId: {
      get() {
        return this.labType === LAB_TYPE.QUESTION
          ? this.questionsLabId
          : this.practiceLabId
      },
      set(value) {
        if (this.labType === LAB_TYPE.QUESTION) {
          this.questionsLabId = value
          return
        }
        this.practiceLabId = value
      },
    },
    hasSelectedLabId() {
      return Boolean(this.normalizeLabId(this.selectedLabId))
    },
    canPreviewExam() {
      const examId = this.exam && this.exam.id
      const hasQuestionsPool =
        Array.isArray(this.exam && this.exam.questions_pool) &&
        this.exam.questions_pool.length > 0
      return Boolean(examId && hasQuestionsPool)
    },
    examPreviewBaseUrl() {
      return this.myIneUrl
    },
    examPreviewUrl() {
      if (!this.exam || !this.exam.id) return ''
      const baseUrl = `${this.examPreviewBaseUrl}/exam/${this.exam.id}/preview`
      return this.previewWithAllQuestions ? `${baseUrl}?full=true` : baseUrl
    },
    isSaveBlockedByDiagnostics() {
      return this.hasBlockingDiagnostics(this.csvDiagnostics)
    },
    showCategoryDiagnostics() {
      return this.selectQuestionsPerObjectivePerQuestionCategory
    },
    csvColumnsGuidance() {
      if (this.showCategoryDiagnostics) {
        return 'Columns: question_text, option_a-d, correct_answer, category, points.'
      }

      return 'Columns: question_text, option_a-d, correct_answer, points. Category is optional in objective-based templates.'
    },
    showPerQuestionTypeSwitches() {
      return this.randomizeQuestionsMode === RANDOMIZE_MODE.BY_QUESTION_TYPE
    },
    showGlobalRandomizeSwitch() {
      return this.randomizeQuestionsMode === RANDOMIZE_MODE.GLOBAL
    },
    showRandomizeModeSelector() {
      return this.supportsPerQuestionTypeRandomization
    },
    computedRandomizeModeOptions() {
      return this.randomizeModeOptions
    },
    supportsPerQuestionTypeRandomization() {
      const exam = this.exam
      const hasLabRelatedQuestions = Array.isArray(
        exam && exam.questions_pool
      )
        ? exam.questions_pool.some((question) =>
            Boolean(question && question.is_lab_related)
          )
        : false
      const hasLabRelatedConfig = Boolean(
        exam &&
          exam.config &&
          (exam.config.lab_related ||
            exam.config.amount_questions_lab_related ||
            exam.config.amount_questions_per_objective_lab_related)
      )
      const hasPerQuestionTypeRandomizationConfig = Boolean(
        exam &&
          exam.config &&
          exam.config.randomize_questions_per_question_type &&
          typeof exam.config.randomize_questions_per_question_type ===
            'object'
      )
      const hasPerQuestionTypeRandomizationFormState =
        this.randomizeQuestionsMode === RANDOMIZE_MODE.BY_QUESTION_TYPE

      return (
        hasLabRelatedQuestions ||
        hasLabRelatedConfig ||
        hasPerQuestionTypeRandomizationConfig ||
        hasPerQuestionTypeRandomizationFormState
      )
    },
  },
}
</script>

<style scoped>
.exam-preview-box {
  border: 1px solid #dfe4ea;
  border-radius: 6px;
  padding: 12px;
}
.input-error /deep/ input {
  border-color: #ea5455 !important;
}
.field-error-msg {
  color: #ea5455;
  display: block;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}
.preview-source {
  color: #0078d4;
  display: block;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}
.randomize-mode-select {
  max-width: 260px;
}

@media (max-width: 1200px) {
  .exam-form {
    flex-wrap: wrap;
    flex-direction: column;
    width: 100%;
  }

  .exam-form .content-left-panel,
  .exam-form .content-right-panel {
    padding-left: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    flex: 0 0 100%;
  }

  .exam-form .content-right-panel {
    margin-top: 0;
    margin-bottom: 2rem;
    order: -1;
  }
}
</style>
